import React from 'react';

import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import LabelChip from 'components/chip/LabelChip';
import { ExpenseSuggestion, searchExpenseSuggestions } from 'stores/ExpenseStoreAdapter';

const NAME_MAX = 128;
const DEBOUNCE_MS = 300;

type Props = {
  value: string;
  onChange: (value: string) => void;
  /** 候補選択時（他項目のプレフィルに使う） */
  onSelectSuggestion: (suggestion: ExpenseSuggestion) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
};

/**
 * 経費名の入力＋候補オートコンプリート（API4141・夢機能）。
 * 入力に応じて searchExpenseSuggestions をデバウンス（300ms）で呼び、
 * 候補を選ぶと金額/数量/店舗/ラベル/メモがプレフィルされる（onSelectSuggestion）。
 * matchType は指定せず送る（前方 OR 後方の両方・BE既定）。
 */
const ExpenseNameAutocomplete: React.FC<Props> = ({
  value,
  onChange,
  onSelectSuggestion,
  error = false,
  helperText,
  disabled = false,
}) => {
  const [options, setOptions] = React.useState<ExpenseSuggestion[]>([]);
  const [inputValue, setInputValue] = React.useState(value);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // 外部 value 変更（プレフィル等）に追従
  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const runSearch = React.useCallback((keyword: string) => {
    const kw = keyword.trim();
    if (!kw) {
      setOptions([]);
      return;
    }
    searchExpenseSuggestions(kw)
      .then(setOptions)
      .catch(() => setOptions([]));
  }, []);

  const handleInputChange = (_: React.SyntheticEvent, newInput: string) => {
    setInputValue(newInput);
    onChange(newInput);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(newInput), DEBOUNCE_MS);
  };

  return (
    <Autocomplete<ExpenseSuggestion, false, false, true>
      freeSolo
      disabled={disabled}
      options={options}
      inputValue={inputValue}
      // freeSolo のため value 管理は inputValue に委譲する
      filterOptions={(x) => x}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
      onInputChange={handleInputChange}
      onChange={(_, selected) => {
        if (selected && typeof selected !== 'string') {
          onChange(selected.name);
          setInputValue(selected.name);
          onSelectSuggestion(selected);
        }
      }}
      renderOption={(optionProps, option) => {
        const { key, ...rest } = optionProps as React.HTMLAttributes<HTMLLIElement> & {
          key: React.Key;
        };
        return (
          <Box component="li" key={String(key)} {...rest} sx={{ display: 'block', py: 0.75 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                {option.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {option.amount.toLocaleString('ja-JP')} 円 × {option.quantity}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
              <LabelChip name={option.labelName} color={option.labelColor} size="small" />
              <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                {option.storeName}
              </Typography>
            </Stack>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="経費名"
          size="small"
          fullWidth
          required
          error={error}
          helperText={helperText}
          placeholder="入力すると過去の記録から候補が出ます"
          inputProps={{ ...params.inputProps, maxLength: NAME_MAX }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      )}
      noOptionsText="候補がありません"
      sx={{ width: '100%' }}
    />
  );
};

export default ExpenseNameAutocomplete;
